
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CustomRole {
  id: string;
  brand_id: string;
  name: string;
  description?: string;
  permissions: string[];
  created_at: string;
  updated_at: string;
}

export const useCustomRoles = (brandId?: string) => {
  const [roles, setRoles] = useState<CustomRole[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchRoles = async () => {
    if (!brandId) return;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('custom_roles')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform the data to ensure permissions is a string array
      const transformedData = (data || []).map(role => ({
        ...role,
        permissions: Array.isArray(role.permissions) 
          ? role.permissions.filter((p): p is string => typeof p === 'string')
          : []
      }));
      
      setRoles(transformedData);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch roles');
    } finally {
      setIsLoading(false);
    }
  };

  const createRole = async (name: string, description: string, permissions: string[]): Promise<boolean> => {
    if (!brandId) return false;

    try {
      const { error } = await supabase
        .from('custom_roles')
        .insert([{
          brand_id: brandId,
          name,
          description,
          permissions
        }]);

      if (error) throw error;
      
      await fetchRoles();
      toast.success('Role created successfully');
      return true;
    } catch (error) {
      console.error('Error creating role:', error);
      toast.error('Failed to create role');
      return false;
    }
  };

  const updateRole = async (roleId: string, name: string, description: string, permissions: string[]): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('custom_roles')
        .update({ name, description, permissions })
        .eq('id', roleId);

      if (error) throw error;
      
      await fetchRoles();
      toast.success('Role updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
      return false;
    }
  };

  const deleteRole = async (roleId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('custom_roles')
        .delete()
        .eq('id', roleId);

      if (error) throw error;
      
      await fetchRoles();
      toast.success('Role deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error('Failed to delete role');
      return false;
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [brandId]);

  return {
    roles,
    isLoading,
    createRole,
    updateRole,
    deleteRole,
    refetch: fetchRoles
  };
};
